import { Col, Popover, Row } from 'antd';
import { AxiosError, AxiosResponse, CancelToken } from 'axios';
import { ValidationErrors } from 'final-form';
import moment, { Moment } from 'moment';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import {
  AttributeI,
  AttributeOptionI,
  AttrInternalType,
  AttrSlugType,
} from '~src/interfaces/attributes.types';
import { CompanyI } from '~src/interfaces/company.interface';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';
import { SessionUserI, UserPermissions } from '~src/interfaces/session.types';
import { extractCoreValues, getUserFullName, User } from '~src/interfaces/user.interface';
import { getAttribute, getAttributes } from '~src/modules/attributes/reducers/attributes.reducer';
import { inviteUser } from '~src/modules/users/actions/user.actions';
import { Otp, UserFormValues } from '~src/modules/users/interfaces';
import { getUsers } from '~src/modules/users/reducers/userManagementReducer';
import { StoreI } from '~src/rootReducer';

import { defaultBaseApi } from '~src/services/ApiController';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import {
  getContentLanguageAttribute,
  getManagerAttribute,
  getSystemLanguageAttribute,
  getTimezoneAttribute,
} from '~src/services/attributes';
import AutoCompleteEmail from '~src/services/autoCompleteEmail';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { translateContent } from '~src/services/translationServices';
import {
  validatorDecimal,
  validatorEmail,
  validatorEmptyOr,
  validatorMaxLength80,
  validatorRequired,
} from '~src/services/validators';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import SecondaryButtonLoading from '~src/shared/buttons/loadingButtons/SecondaryButtonLoading';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import Dialog from '~src/shared/dialogs/dialog/Dialog';
import CustomDatePicker from '~src/shared/form/inputs/CustomDatePicker';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import SelectInput from '~src/shared/form/select/SelectInput';
import SelectSearch from '~src/shared/form/select/SelectSearch/SelectSearch';
import { AnyArrayOfObj } from '~src/shared/form/select/SelectSearch/selectSearch.interfaces';
import {
  ActionRow,
  ButtonSection,
  Footer,
  SectionBody,
  SectionHeader,
  SubSection,
} from '~src/shared/form/shared.sc';
import ImageDropZone from '~src/shared/imageUploader/ImageDropZone';
import OnChange from '~src/shared/reactFinalForm/OnChange';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { showNotification } from '~src/store/app/actions';
import { ShowNotification } from '~src/store/app/interfaces';
import { getUserDetails, updateSessionUserAttr } from '~src/store/session/actions';
import { FieldType } from '~src/types';

import OtpCreatedDialogContent from './OtpCreatedDialogContent';
import SettingsAttributeSection from './SettingsAttributeSection';
import {
  ErrorCol,
  FieldWrapper,
  ImageDZContainer,
  InputsContainer,
  RelativeContainer,
  TemplateError,
} from './userForm.sc';

export interface AdditionalSelectProps {
  list: Record<string, any>;
  attrs: AttributeOptionI[];
}

interface OwnProps {
  onCancel: () => void;
  onDelete?: (user: User) => Promise<void>;
  onFrmSubmit: (values: UserFormValues, initialValues: Partial<UserFormValues>) => Promise<void>;
  onDeactivate?: (user: User) => Promise<void>;
  user?: User | null;
  form?: string;
}

interface StateProps {
  loggedInUser: SessionUserI | null;
  users: User[] | null;
  attributes: AttributeI[];
  managerAttribute: AttributeI;
  timezoneAttribute: AttributeI;
  systemLanguageAttribute: AttributeI;
  contentLanguageAttribute: AttributeI;
  deleteLoading?: boolean;
  deactivationLoading: boolean;
  isOwnUser?: boolean | null;
  isDeactivated?: boolean;
  allAttributes?: Record<string, AttributeI>;
  initialValues: Partial<UserFormValues>;
  loadingSaveAdd?: boolean;
  userDetails?: User | null;
  company: CompanyI | null;
}

type CorePropsType = Pick<
  StateProps,
  | 'loggedInUser'
  | 'users'
  | 'attributes'
  | 'managerAttribute'
  | 'timezoneAttribute'
  | 'systemLanguageAttribute'
  | 'contentLanguageAttribute'
>;

type CoreStateProps = Pick<
  StateProps,
  | 'loggedInUser'
  | 'users'
  | 'attributes'
  | 'managerAttribute'
  | 'timezoneAttribute'
  | 'systemLanguageAttribute'
  | 'contentLanguageAttribute'
>;

interface DispatchProps {
  updateSessionUserAttr: (key: string, value: string | number | null) => AnyAction;
  getUserDetails: () => AnyAction;
  inviteUser: (userId: string, section: 'okr' | 'survey' | 'spaceloop') => ApiReduxResp;
  showNotification: ShowNotification;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

interface State {
  confirmDeletion: boolean;
  confirmDeactivation: boolean;
  confirmActivation: boolean;
  userEmail: null | string;
  showTooltip: boolean;
  inviting: boolean;
  otp: string;
}

class UserForm extends React.Component<Props, State> {
  noManagerOpt = { name: this.props.t('x.users.no_supervisor'), value: '-' };
  autoCompleteEmail = new AutoCompleteEmail(this.props.loggedInUser!);

  constructor(props: Props) {
    super(props);
    this.state = {
      confirmDeletion: false,
      confirmDeactivation: false,
      confirmActivation: false,
      userEmail: null,
      showTooltip: false,
      inviting: false,
      otp: '',
    };
  }

  onDeleteUser = () => {
    if (this.props.user && this.props.onDelete) {
      this.props.onDelete(this.props.user);
    }
  };

  onDeactivateUser = () => {
    if (this.props.user && this.props.onDeactivate) {
      this.props.onDeactivate(this.props.user);
    }
  };

  mapAttributeValues = async (passedValues: UserFormValues) => {
    const { attributes } = passedValues;
    const settingAttributes = this.props.user ? this.props.user.setting_attributes : null;
    const mappedValues: UserFormValues = {
      ...passedValues,
      attributes: Object.keys(attributes).reduce((acc: typeof attributes, key: string) => {
        const attr = attributes[key];
        if (attr === '' || attr === '-' || attr === 'Invalid date') {
          acc[key] = null;
        } else {
          acc[key] = attr;
        }

        return acc;
      }, {}),
    };

    const rsp = await this.props.onFrmSubmit(mappedValues, this.props.initialValues);
    const { isOwnUser, allAttributes, getUserDetails } = this.props;
    if (isOwnUser && allAttributes !== undefined) {
      getUserDetails();
      const systemLanguageAttr = Object.values(allAttributes).find(
        ({ slug }) => slug === AttrSlugType.SystemLanguage,
      );
      const contentLanguageAttr = Object.values(allAttributes).find(
        ({ slug }) => slug === AttrSlugType.ContentLanguage,
      );
      if (
        systemLanguageAttr &&
        settingAttributes &&
        settingAttributes[systemLanguageAttr.uuid] !== mappedValues.attributes[systemLanguageAttr.uuid]
      ) {
        this.props.updateSessionUserAttr(
          systemLanguageAttr.uuid,
          mappedValues.attributes[systemLanguageAttr.uuid],
        );
      }
      if (
        contentLanguageAttr &&
        settingAttributes &&
        settingAttributes[contentLanguageAttr.uuid] !== mappedValues.attributes[contentLanguageAttr.uuid]
      ) {
        this.props.updateSessionUserAttr(
          contentLanguageAttr.uuid,
          mappedValues.attributes[contentLanguageAttr.uuid],
        );
      }
    }

    return rsp;
  };

  toggleConfirmDeletionModal = () => {
    this.setState((prevState) => ({ confirmDeletion: !prevState.confirmDeletion }));
  };

  confirmDeactivationModal = () => {
    this.setState((prevState) => ({ confirmDeactivation: !prevState.confirmDeactivation }));
  };

  confirmActivationModal = () => {
    this.setState((prevState) => ({ confirmActivation: !prevState.confirmActivation }));
  };

  hostSuggest = (event: any) => {
    if (this.props.form === 'add_user_form') {
      this.autoCompleteEmail.hotSuggestion(event.currentTarget.value);
      this.setState({ userEmail: this.autoCompleteEmail.user.email });
      if (event.currentTarget.value === '') {
        this.autoCompleteEmail.reset();
      }
    }
  };

  mapSearchUsersData = (data: AnyArrayOfObj): Array<{ name: string; value: string }> =>
    data
      .reduce((acc: Array<{ name: string; value: string }>, item) => {
        if (!this.props.user || this.props.user.uuid !== item.id) {
          acc.push({ name: getUserFullName(item.meta), value: item.id });
        }
        return acc;
      }, [])
      .sort((a, b) => (a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1));

  renderSelectInput = ({
    input,
    list,
    attrs,
    label,
    labelKey,
    meta: { touched, error },
  }: FieldType & AdditionalSelectProps) => {
    let optList: Array<{ name: string; value: string }> | undefined;
    let defaultValue = '';
    if (attrs) {
      optList = attrs.map((item) => ({
        name: translateContent(item.value),
        value: item.uuid,
      }));
    } else {
      optList = Object.keys(list).map((k) => ({ name: list[k], value: k }));
      defaultValue = input.value;
    }
    optList = optList.sort((a, b) => {
      const textA = a.name.toUpperCase();
      const textB = b.name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
    if (attrs) {
      optList.unshift({ name: this.props.t('x.users.select.empty'), value: '-' });
    }

    return (
      <SelectInput
        {...input}
        t={this.props.t}
        label={label}
        optList={optList}
        defaultValue={defaultValue}
        error={touched ? error : null}
        labelKey={labelKey}
      />
    );
  };

  renderField({ autoFocus, input, label, meta: { touched, error }, labelKey }: FieldType) {
    return (
      <CustomFormInput
        {...input}
        autoFocus={autoFocus}
        error={touched ? error : null}
        label={label}
        labelKey={labelKey}
      />
    );
  }

  renderDecimalInput = ({ input, label, meta: { touched, error }, labelKey }: FieldType) => {
    return (
      <CustomFormInput
        {...input}
        error={touched ? error : null}
        label={label}
        type="number"
        step="any"
        labelKey={labelKey}
      />
    );
  };

  validateIdentifiers = (values: { email?: string; username?: string }): ValidationErrors =>
    !values.email && !values.username ? { email: 'email_or_username_required' } : undefined;

  showCreateOtpPopup = () => {
    if (!this.props.user) {
      return;
    }
    defaultBaseApi
      .passwordCreateOtp(this.props.user.uuid)
      .then((res: AxiosResponse<Otp>) => {
        this.setState((prevState) => {
          return {
            ...prevState,
            otp: res.data.value,
          };
        });
      })
      .catch((err: AxiosError) => {
        console.log(err); // TODO
      });
  };

  closeOtp = () => {
    this.setState((prevState) => {
      return {
        ...prevState,
        otp: '',
      };
    });
  };

  render() {
    const { t } = this.props;
    const existingUser = this.props.user && this.props.form !== 'add_user_form';
    const deactivationToggleButtonLabel =
      this.props.user && this.props.user.deactivated ? 'x.button.activate' : 'x.button.deactivate';

    const { user, managerAttribute } = this.props;
    const currManagerData = user && user.manager_attributes && user.manager_attributes[managerAttribute.uuid];

    const currManagerOpt = currManagerData
      ? {
          value: currManagerData.uuid,
          name: getUserFullName(currManagerData),
        }
      : undefined;

    return (
      <>
        <Form
          initialValues={this.props.initialValues}
          onSubmit={this.mapAttributeValues}
          validate={this.validateIdentifiers}
        >
          {({
            handleSubmit,
            form,
            submitting,
            pristine,
            submitFailed,
            hasValidationErrors,
            hasSubmitErrors,
            dirtySinceLastSubmit,
          }) => {
            const reset = () => {
              form.reset();
              this.props.onCancel();
            };
            const toggleInviting = () => {
              this.setState((prevState) => ({ inviting: !prevState.inviting }));
            };

            const inviteSpaceloopUser = (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              if (!this.props.user) {
                return;
              }
              toggleInviting();
              this.props
                .inviteUser(this.props.user.uuid, 'spaceloop')
                .then(({ response }) => {
                  if (response.status >= 400) {
                    this.props.showNotification({
                      id: 'invitation_error',
                      txtKey: 'x.users.user_invitation_failed',
                      type: 'error',
                      persistent: false,
                    });

                    return;
                  }
                  this.props.showNotification({
                    id: 'invitation_success',
                    txtKey: 'x.users.user_invitation_successful',
                    type: 'info',
                    persistent: false,
                  });
                  this.props.onCancel();
                })
                .finally(toggleInviting);
            };

            const showSpaceloopInviteButton = () => {
              return (
                this.props.company &&
                this.props.company.features.booking &&
                this.props.user &&
                (!this.props.user.userPermissions || this.props.user.userPermissions.length === 0)
              );
            };

            const acceptHotSuggest = (event: any) => {
              if (event.keyCode === 9) {
                if (this.autoCompleteEmail.user.email !== '') {
                  form.change('email', this.autoCompleteEmail.user.email);
                }
                this.autoCompleteEmail.reset();
              }
            };

            const registerHotSuggestKey = () => {
              if (this.props.form === 'add_user_form') {
                document.addEventListener('keydown', acceptHotSuggest);
                this.setState({ showTooltip: true });
              }
            };

            const unregisterHotSuggestKey = () => {
              document.removeEventListener('keydown', acceptHotSuggest);
              this.setState({ showTooltip: false });
            };

            const customDateOnChange = (date: Moment | null, name: keyof UserFormValues) => {
              const newDate = date === null ? '' : moment(date).format('YYYY-MM-DD');
              form.change(name, newDate);
            };

            const checkDateFormat = (value: string | null) => {
              if (value) {
                if (value !== 'Invalid date') {
                  return moment(value);
                }
                return value;
              }
              return null;
            };

            const renderDatePicker = ({ input, label, meta: { touched, error }, labelKey }: FieldType) => {
              const onChangeDatePicker = (date: Moment | null) =>
                customDateOnChange(date, input.name as keyof UserFormValues);
              return (
                <CustomDatePicker
                  error={touched ? error : undefined}
                  dateValue={checkDateFormat(input.value)}
                  label={label}
                  labelKey={labelKey}
                  onChange={onChangeDatePicker}
                />
              );
            };

            const autoComplete = (value: string) => {
              if (this.props.form === 'add_user_form') {
                this.autoCompleteEmail.autoComplete(value);

                if (this.autoCompleteEmail.user.firstname !== '') {
                  form.change('firstname', this.autoCompleteEmail.user.firstname);
                }
                if (this.autoCompleteEmail.user.lastname !== '') {
                  form.change('lastname', this.autoCompleteEmail.user.lastname);
                }
                this.autoCompleteEmail.reset();
                unregisterHotSuggestKey();
              }
            };

            const fetchSearchSuggestions = (
              searchToken: string,
              cancelToken: CancelToken,
            ): Promise<Segment[]> => {
              return fetchSearchSegments(searchToken, cancelToken, [
                SegmentType.Company,
                SegmentType.Group,
                SegmentType.User,
              ]);
            };

            return (
              <form onSubmit={handleSubmit}>
                {this.props.user && (
                  <Dialog
                    show={!!this.state.otp}
                    onHide={this.closeOtp}
                    children={
                      <OtpCreatedDialogContent
                        onHide={this.closeOtp}
                        otp={this.state.otp}
                        user={this.props.user}
                      />
                    }
                  />
                )}
                <ActionConfirmDialog
                  t={t}
                  show={this.state.confirmDeletion}
                  close
                  onHide={this.toggleConfirmDeletionModal}
                  showDeletion={!!this.props.user}
                  actionTitleKey="x.users.delete_confirmation.headline"
                  actionContentKey="x.users.delete_confirmation.content"
                  actionCall={this.onDeleteUser}
                  loadingAction={this.props.deleteLoading}
                />
                <ActionConfirmDialog
                  t={t}
                  show={this.state.confirmDeactivation && !!this.props.user && !this.props.user.deactivated}
                  close
                  onHide={this.confirmDeactivationModal}
                  showDeletion={!!this.props.user}
                  actionTitleKey="x.users.deactivation_confirmation.headline"
                  actionContentKey="x.users.deactivation_confirmation.content"
                  actionCall={this.onDeactivateUser}
                  loadingAction={this.props.deactivationLoading}
                  btnOkKey={deactivationToggleButtonLabel}
                />
                <ActionConfirmDialog
                  t={t}
                  show={this.state.confirmActivation && !!this.props.user && this.props.user.deactivated}
                  close
                  onHide={this.confirmActivationModal}
                  showDeletion={!!this.props.user}
                  actionTitleKey="x.users.activation_confirmation.headline"
                  actionContentKey="x.users.activation_confirmation.content"
                  actionCall={this.onDeactivateUser}
                  loadingAction={this.props.deactivationLoading}
                  btnOkKey={deactivationToggleButtonLabel}
                />
                <SectionHeader data-translation="x.users.basic_information">
                  {t('x.users.basic_information')}
                </SectionHeader>
                <SectionBody>
                  <SubSection>
                    <Col lg={8}>
                      <RelativeContainer>
                        <Popover
                          content={
                            <div style={{ maxWidth: 350 }}>
                              {t('x.users.email_suggest.info_box', { email: this.state.userEmail })}
                            </div>
                          }
                          trigger="click"
                          placement="topLeft"
                          visible={!!this.state.userEmail && this.state.showTooltip}
                        />
                        <Field
                          autoFocus
                          name="email"
                          label={t('x.email')}
                          type="email"
                          children={this.renderField}
                          validate={validatorEmptyOr(
                            composeValidators([validatorEmail, validatorMaxLength80]),
                          )}
                          labelKey="x.email"
                          onFocus={registerHotSuggestKey}
                          onChange={this.hostSuggest}
                          parse={(value) => value}
                        />
                        <OnChange name="email">{autoComplete}</OnChange>
                      </RelativeContainer>
                    </Col>
                    <Col lg={8}>
                      <Field
                        name="username"
                        label={t('x.users.form.username.label')}
                        type="text"
                        children={this.renderField}
                        validate={validatorMaxLength80}
                        labelKey="x.users.form.username.label"
                        parse={(value) => value}
                      />
                    </Col>
                    <Col lg={8}>
                      <SelectSearch
                        t={t}
                        inputName={`attributes.${this.props.managerAttribute.uuid}`}
                        currItemOpt={currManagerOpt || this.noManagerOpt}
                        noSelectedOpt={this.noManagerOpt}
                        fetchSearchSuggestions={fetchSearchSuggestions}
                        mapData={this.mapSearchUsersData}
                      />
                    </Col>
                  </SubSection>
                  <SubSection>
                    <Col lg={8}>
                      <Field
                        name="firstname"
                        label={t('x.firstname')}
                        type="text"
                        children={this.renderField}
                        validate={composeValidators([validatorRequired, validatorMaxLength80])}
                        labelKey="x.firstname"
                      />
                    </Col>
                    <Col lg={8}>
                      <Field
                        name="lastname"
                        label={t('x.lastname')}
                        type="text"
                        children={this.renderField}
                        validate={composeValidators([validatorRequired, validatorMaxLength80])}
                        labelKey="x.lastname"
                      />
                    </Col>
                    <Col lg={8}>
                      {this.props.user && (
                        <ImageDZContainer>
                          <ImageDropZone
                            disabled={this.props.form === 'add_user_form'}
                            disabledMessage={{
                              txt: t('x.users.form.image_dropzone.disabled.message'),
                              key: 'x.users.form.image_dropzone.disabled.message',
                            }}
                            isInForm
                            userId={this.props.user.uuid}
                            type="user"
                            fromWhere="userManagement"
                          />
                        </ImageDZContainer>
                      )}
                    </Col>
                  </SubSection>
                </SectionBody>
                {this.props.attributes.length !== 0 && (
                  <>
                    <SectionHeader data-translation="x.users.additional_attributes">
                      {t('x.users.additional_attributes')}
                    </SectionHeader>
                    <SectionBody>
                      <InputsContainer>
                        {this.props.attributes.map((attribute: AttributeI) => {
                          switch (attribute.internal_type) {
                            case AttrInternalType.Option:
                              return (
                                <FieldWrapper key={attribute.uuid}>
                                  <Field
                                    name={`attributes.${attribute.uuid}`}
                                    label={translateContent(attribute.name)}
                                    children={this.renderSelectInput}
                                    attrs={attribute.attribute_options}
                                    labelKey="BE"
                                  />
                                </FieldWrapper>
                              );
                            case AttrInternalType.Datetime:
                              return (
                                <FieldWrapper key={attribute.uuid}>
                                  <Field
                                    name={`attributes.${attribute.uuid}`}
                                    label={translateContent(attribute.name)}
                                    children={renderDatePicker}
                                    labelKey="BE"
                                  />
                                </FieldWrapper>
                              );
                            case AttrInternalType.Decimal:
                              return (
                                <FieldWrapper key={attribute.uuid}>
                                  <Field
                                    name={`attributes.${attribute.uuid}`}
                                    label={translateContent(attribute.name)}
                                    validate={validatorDecimal}
                                    children={this.renderDecimalInput}
                                    labelKey="BE"
                                  />
                                </FieldWrapper>
                              );
                            default:
                              return null;
                          }
                        })}
                      </InputsContainer>
                    </SectionBody>
                  </>
                )}
                <SettingsAttributeSection
                  t={t}
                  timezoneAttribute={this.props.timezoneAttribute}
                  systemLanguageAttribute={this.props.systemLanguageAttribute}
                  contentLanguageAttribute={this.props.contentLanguageAttribute}
                  renderSelectInput={this.renderSelectInput}
                  isOwnUser={this.props.isOwnUser}
                />

                <Footer>
                  <ActionRow>
                    <ButtonSection $alignment="left">
                      {existingUser && (
                        <SecondaryButton
                          type="button"
                          onClick={this.showCreateOtpPopup}
                          disabled={submitting}
                        >
                          <span data-translation="x.users.form.button.create-otp">
                            {t('x.users.form.button.create-otp')}
                          </span>
                        </SecondaryButton>
                      )}
                      {existingUser && !this.props.isOwnUser && (
                        <SecondaryButtonLoading
                          $buttonColor="reddish"
                          type="button"
                          onClick={this.toggleConfirmDeletionModal}
                          isLoading={!!this.props.deleteLoading}
                          disabled={submitting}
                        >
                          <span data-translation="x.button.delete">{t('x.button.delete')}</span>
                        </SecondaryButtonLoading>
                      )}
                      {this.props.user && this.props.form !== 'add_user_form' && (
                        <SecondaryButtonLoading
                          $buttonColor="lightblue"
                          type="button"
                          onClick={
                            !this.props.user.deactivated
                              ? this.confirmDeactivationModal
                              : this.confirmActivationModal
                          }
                          isLoading={this.props.deactivationLoading}
                          disabled={submitting}
                        >
                          <span data-translation={deactivationToggleButtonLabel}>
                            {t(deactivationToggleButtonLabel)}
                          </span>
                        </SecondaryButtonLoading>
                      )}
                      {showSpaceloopInviteButton() && (
                        <PrimaryButtonLoading
                          isLoading={this.state.inviting}
                          onClick={inviteSpaceloopUser}
                          $margin="0 5px 0 0"
                          disabled={submitting}
                        >
                          <span data-translation="x.button.invite_to_spaceloop">
                            {t('x.button.invite_to_spaceloop')}
                          </span>
                        </PrimaryButtonLoading>
                      )}
                    </ButtonSection>
                    {
                      <ButtonSection $alignment="right">
                        <SecondaryButton onClick={reset} type="reset" disabled={submitting}>
                          <span data-translation="x.button.cancel">{t('x.button.cancel')}</span>
                        </SecondaryButton>
                        <PrimaryButtonLoading
                          isLoading={submitting}
                          disabled={
                            submitting ||
                            pristine ||
                            hasValidationErrors ||
                            (hasSubmitErrors && !dirtySinceLastSubmit)
                          }
                        >
                          <span data-translation="x.button.save">
                            {existingUser ? t('x.button.updateUser') : t('x.button.addUser')}
                          </span>
                        </PrimaryButtonLoading>
                      </ButtonSection>
                    }
                  </ActionRow>
                  {submitFailed && (
                    <Row>
                      <ErrorCol lg={24}>
                        {' '}
                        <TemplateError data-translation="x.surveys.schedules.template_error">
                          {t('x.surveys.schedules.template_error')}
                        </TemplateError>
                      </ErrorCol>
                    </Row>
                  )}
                </Footer>
              </form>
            );
          }}
        </Form>
      </>
    );
  }
}

function getDefaultAttributeValuesFromState(state: StoreI, coreProps: CoreStateProps) {
  const { company } = state.user.session;
  if (company) {
    const { setting_attributes } = company;
    const { timezoneAttribute, systemLanguageAttribute, contentLanguageAttribute } = coreProps;
    return {
      [timezoneAttribute.uuid]: setting_attributes[timezoneAttribute.uuid],
      [systemLanguageAttribute.uuid]: setting_attributes[systemLanguageAttribute.uuid],
      [contentLanguageAttribute.uuid]: setting_attributes[contentLanguageAttribute.uuid],
    };
  } else {
    return {};
  }
}

function getAttributeValuesFromState(state: StoreI, ownProps: OwnProps, coreProps: CoreStateProps) {
  const { user } = ownProps;
  const { managerAttribute } = coreProps;
  const values: Record<string, string | number | null> = {};
  const currManagerAttr = user && user.manager_attributes && user.manager_attributes[managerAttribute.uuid];

  values[managerAttribute.uuid] = currManagerAttr ? currManagerAttr.uuid : '-';
  if (user && user.custom_attributes && Object.keys(user.custom_attributes).length > 0) {
    Object.keys(user.custom_attributes).forEach((key) => {
      const attribute = getAttribute(state, key);
      let newValue = user.custom_attributes ? user.custom_attributes[key] : null;
      if (attribute.internal_type === 'datetime' && newValue !== null) {
        newValue = moment(newValue).format('YYYY-MM-DD');
      }
      values[key] = newValue;
    });
  }
  return {
    ...getDefaultAttributeValuesFromState(state, coreProps),
    ...values,
    ...(user ? user.setting_attributes : null),
  };
}

const getPermissionsFromState = ({ user }: OwnProps) => ({
  admin: user && user.userPermissions ? user.userPermissions.includes(UserPermissions.ManageCompany) : false,
});

function mapStateToProps(state: StoreI, ownProps: OwnProps): StateProps {
  const coreProps: CorePropsType = {
    loggedInUser: state.user.session.user,
    users: getUsers(state),
    attributes: getAttributes(state),
    managerAttribute: getManagerAttribute(state),
    timezoneAttribute: getTimezoneAttribute(state),
    systemLanguageAttribute: getSystemLanguageAttribute(state),
    contentLanguageAttribute: getContentLanguageAttribute(state),
  };

  const initialValues = {
    attributes: getDefaultAttributeValuesFromState(state, coreProps),
    permissions: { admin: false },
  };

  if (!ownProps.user) {
    return {
      ...coreProps,
      isOwnUser: false,
      initialValues,
      loadingSaveAdd: state.userManagement.addUserLoading,
      company: null,
      deactivationLoading: false,
    };
  }

  return {
    ...coreProps,
    allAttributes: state.attributes.byId,
    userDetails: state.userManagement.userDetails,
    isOwnUser: state.user.session.user && state.user.session.user.uuid === ownProps.user.uuid,
    deleteLoading: state.userManagement.deleteLoading,
    initialValues: {
      ...initialValues,
      ...extractCoreValues(ownProps.user),
      attributes: getAttributeValuesFromState(state, ownProps, coreProps),
      permissions: getPermissionsFromState(ownProps),
    },
    company: state.user.session.company,
    isDeactivated: (state.user.session.user && state.user.session.user.deactivated) || false,
    deactivationLoading: state.userManagement.changingActiveStatusLoading,
  };
}

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  updateSessionUserAttr: (languageKey, newLanguage) =>
    dispatch(updateSessionUserAttr(languageKey, newLanguage)),
  getUserDetails: () => dispatch(getUserDetails()),
  inviteUser: (userId: string, section: 'okr' | 'survey' | 'spaceloop') =>
    dispatch(inviteUser(userId, section)),
  showNotification: (notification) => dispatch(showNotification(notification)),
});

const UserFormT = withTranslation()(UserForm);
const UserFormC = connect(mapStateToProps, mapDispatchToProps)(UserFormT);

export default UserFormC;
